<template>
    <div class="dk-offer-card tw-rounded tw-border tw-border-solid tw-border-border-color tw-bg-white">
        <div
            class="dk-offer-card__content tw-flex max-lg:tw-flex-col tw-items-stretch tw-justify-between max-lg:tw-gap-4 tw-p-4 lg:tw-p-6"
        >
            <div class="dk-offer-card__content-left tw-flex tw-shrink tw-flex-col tw-gap-4">
                <div class="tw-flex tw-items-center tw-gap-2 lg:tw-gap-4 tw-flex-wrap">
                    <template v-if="firstItemHasBadge">
                        <dk-badge class="dk-offer-card__badge lg:tw-hidden" size="small" type="light-blue">
                            <span class="dk-offer-card__badge-text tw-mr-2">{{ firstItem.type.text }}</span>
                            <dk-icon name="ds-tag-price-filled" size="xs" color="blue-700" />
                        </dk-badge>
                        <dk-badge class="dk-offer-card__badge tw-hidden lg:tw-inline-flex" type="light-blue">
                            <span class="dk-offer-card__badge-text tw-mr-2">{{ firstItem.type.text }}</span>
                            <dk-icon name="ds-tag-price-filled" size="xs" color="blue-700" />
                        </dk-badge>
                    </template>
                    <template v-if="firstItemHasBenefits">
                        <dk-badge
                            class="dk-offer-card__badge lg:tw-hidden hover:tw-bg-brand-primary hover:tw-text-white hover:tw-cursor-pointer"
                            size="small"
                            type="light-blue"
                            @click.native="openBenefitsModal(firstItem)"
                        >
                            <span class="dk-offer-card__badge-text tw-mr-2 tw-underline">
                                {{ firstItem.benefits.length }} benefits
                            </span>
                            <dk-icon name="ds-info-filled" size="xs" color="inherit" />
                        </dk-badge>
                        <dk-badge
                            class="dk-offer-card__badge tw-hidden lg:tw-inline-flex hover:tw-bg-brand-primary hover:tw-text-white hover:tw-cursor-pointer"
                            type="light-blue"
                            @click.native="openBenefitsModal(firstItem)"
                        >
                            <span class="dk-offer-card__badge-text tw-mr-2 tw-underline">
                                {{ firstItem.benefits.length }} benefits
                            </span>
                            <dk-icon name="ds-info-filled" size="xs" color="inherit" />
                        </dk-badge>
                    </template>
                </div>
                <div class="dk-offer-card__info tw-flex tw-flex-col tw-gap-1 lg:tw-gap-2">
                    <div class="dk-offer-card__title">
                        <span class="tw-text-lg tw-leading-base lg:tw-text-3xl lg:tw-leading-xl tw-font-bold">
                            {{ firstItem.title }}
                        </span>
                    </div>
                    <div v-if="firstItemHasDetails" class="dk-offer-card__details">
                        <ul class="dk-offer-card__detail-list tw-flex max-lg:tw-flex-col tw-list-inside">
                            <li
                                v-for="(detail, index) in firstItem.details"
                                :key="`detail-${index}`"
                                :class="`dk-offer-card__detail-list-item${index}`"
                            >
                                <span class="dk-offer-card__detail-list-disc tw-mx-2 lg:tw-hidden tw-font-extrabold">
                                    ·
                                </span>
                                <span
                                    class="dk-offer-card__detail-list-text tw-text-base tw-leading-base lg:tw-text-xl lg:tw-leading-lg"
                                >
                                    {{ detail.text }}
                                </span>
                                <span
                                    v-if="index < firstItem.details.length - 1"
                                    class="dk-offer-card__detail-list-separator tw-mx-1 max-lg:tw-hidden tw-text-xl tw-leading-lg"
                                >
                                    |
                                </span>
                            </li>
                        </ul>
                    </div>
                    <div v-if="firstItem.subtitle" class="dk-offer-card__subtitle">
                        <span
                            class="tw-text-base tw-leading-base lg:tw-text-lg lg:tw-leading-base"
                            :class="{ 'tw-font-semibold': firstItemHasDetails }"
                        >
                            {{ firstItem.subtitle }}
                        </span>
                    </div>
                    <div v-if="firstItem.description" class="dk-offer-card__description">
                        <span
                            class="dk-offer-card__description-text max-lg:tw-flex max-lg:tw-justify-between max-lg:tw-items-center tw-text-base-subtle tw-text-xs tw-leading-xs lg:tw-text-base lg:tw-leading-base [&>a]:tw-ml-1 [&>a]:tw-font-bold [&>a]:tw-text-brand-primary [&>a]:max-lg:tw-text-sm [&>a]:max-lg:tw-leading-sm"
                            v-html="firstItem.description"
                        ></span>
                    </div>
                    <ul
                        v-if="firstItem.benefits.length > 0"
                        class="dk-offer-card__benefits tw-flex max-lg:tw-flex-col tw-gap-4 lg:tw-gap-6 tw-my-2"
                    >
                        <li
                            v-for="(benefit, i) in firstItem.benefits"
                            :key="i"
                            class="ds-offer-card__benefit-item tw-inline-flex tw-items-center"
                        >
                            <dk-icon :name="`ds-${benefit.icon}`" size="s" class="tw-mr-2" />
                            <span>{{ benefit.label }}</span>
                        </li>
                    </ul>

                    <dk-button
                        v-if="firstItem.href"
                        :href="firstItem.href"
                        variant="ghost"
                        class="dk-offer-card__details-button tw-self-start !tw-text-brand-primary !tw-px-0 max-lg:tw-text-sm max-lg:tw-leading-xs"
                        @click="trackSeeOtherDetails(firstItem)"
                    >
                        {{ $t('ds-offer-card.see-other-details') }}
                    </dk-button>
                </div>
            </div>
            <div
                class="dk-offer-card__content-right tw-flex tw-shrink-0 tw-flex-col lg:tw-items-end tw-justify-between max-lg:tw-gap-2"
            >
                <div v-if="firstItem.badge.length" class="tw-flex tw-items-center tw-gap-2 lg:tw-gap-4 tw-flex-wrap">
                    <template v-for="(badge, index) in firstItem.badge">
                        <dk-badge
                            v-if="badge.id && badge.label"
                            :key="`badge-${index}-mobile`"
                            class="dk-offer-card__badge lg:tw-hidden"
                            :size="'small'"
                            :name="badge.id"
                            :has-icon="false"
                        >
                            <span class="ts--uppercase">{{ badge.label }}</span>
                        </dk-badge>
                        <dk-badge
                            v-if="badge.id && badge.label"
                            :key="`badge-${index}-desktop`"
                            class="dk-offer-card__badge tw-hidden lg:tw-inline-flex"
                            :size="'default'"
                            :name="badge.id"
                            :has-icon="false"
                        >
                            <span class="ts--uppercase">{{ badge.label }}</span>
                        </dk-badge>
                    </template>
                </div>
                <div class="dk-offer-card__pricing tw-flex tw-flex-col lg:tw-items-end tw-gap-1 lg:tw-gap-2">
                    <div
                        class="dk-offer-card__price tw-flex tw-items-end tw-gap-1"
                        @click="trackHighlightedPrice(firstItem)"
                    >
                        <span
                            v-if="firstItem.pricePrefix"
                            class="dk-offer-card__price-prefix max-lg:tw-mb-[2px] tw-text-xs tw-leading-xs lg:tw-text-xl lg:tw-leading-base"
                        >
                            {{ firstItem.pricePrefix }}
                        </span>
                        <span
                            class="dk-offer-card__price-final tw-text-base lg:tw-text-3xl tw-leading-base lg:tw-leading-xl tw-font-bold"
                        >
                            {{ firstItem.finalPrice || firstItem.originalPrice }}
                        </span>
                        <span
                            class="dk-offer-card__price-suffix max-lg:tw-mb-[2px] tw-text-xs tw-leading-xs lg:tw-text-xl lg:tw-leading-base"
                        >
                            {{ firstItem.priceSuffix }}
                        </span>
                    </div>
                    <div v-if="downPaymentLabel" class="dk-offer-card__down-payment">
                        <span
                            class="dk-offer-card__down-payment-label tw-text-xl tw-leading-lg lg:tw-font-semibold tw-hidden lg:tw-inline-block"
                        >
                            {{ downPaymentLabel }}
                        </span>
                        <span class="lg:tw-hidden">
                            <span class="dk-offer-card__down-payment-prefix tw-text-sm tw-leading-sm">
                                {{ downPaymentLabelPrefix }}
                            </span>
                            <span class="dk-offer-card__down-payment-text tw-text-sm tw-leading-sm">
                                {{ downPaymentLabelText }}
                            </span>
                        </span>
                    </div>
                </div>
                <div
                    v-if="firstItem.offerLogo"
                    class="dk-offer-card__partner tw-flex tw-items-center tw-gap-4 max-lg:tw-mt-2"
                >
                    <img
                        v-if="firstItem.offerLogo && firstItem.offerLogo.url"
                        class="tw-max-w-full tw-h-10 tw-m-1"
                        :src="firstItem.offerLogo.url"
                        :alt="firstItem.offerLogo.text"
                    >
                    <span
                        v-if="firstItem.offerLogo && firstItem.offerLogo.official"
                        class="tw-text-xs tw-leading-xs tw-font-bold tw-uppercase"
                    >
                        {{ $t('ds-offer-card.official-promo') }}
                    </span>
                </div>
                <dk-button
                    class="dk-offer-card__select-button lg:tw-min-w-[192px] max-lg:tw-mt-4"
                    variant="primary"
                    @click="onSelect({ ...firstItem, index: itemIndex }, $event)"
                >
                    <span>{{ btnText(firstItem.selected) }}</span>
                </dk-button>
            </div>
        </div>
        <div
            v-if="subItems.length"
            class="dk-offer-card__footer tw-rounded-b hover:tw-bg-secondary-light hover:tw-border-slate-500 tw-overflow-hidden tw-duration-300"
        >
            <div
                class="dk-offer-card__accordion-toggler tw-flex tw-items-center tw-justify-between tw-p-4 lg:tw-p-6 tw-border tw-border-y-border-color tw-border-x-negative tw-border-solid tw-cursor-pointer"
                @click="toggleCollapse"
            >
                <span class="tw-text-base lg:tw-text-xl tw-leading-base lg:tw-leading-lg tw-font-semibold">
                    {{ $tc('ds-offer-card.other-offers', subItems.length, { n: subItems.length }) }}
                </span>
                <dk-icon :name="!collapsed ? 'ds-chevron-up' : 'ds-chevron-down'" size="m" color="basic" />
            </div>
            <div v-show="!collapsed" class="dk-offer-card__accordion">
                <div class="dk-offer-card__accordion-items">
                    <div
                        v-for="(subItem, index) in subItems"
                        :key="`sub-item-${index}`"
                        class="tw-py-4 tw-px-4 lg:tw-px-6"
                        :class="[
                            `dk-offer-card__accordion-item-${index}`,
                            { 'tw-border-b tw-border-solid tw-border-border-color': index !== subItems.length - 1 },
                        ]"
                    >
                        <div
                            class="dk-offer-card__accordion-item-content tw-flex tw-flex-col lg:tw-flex-row tw-justify-center lg:tw-justify-between max-lg:tw-gap-6"
                        >
                            <div class="dk-offer-card__accordion-item-details tw-flex tw-flex-col tw-gap-1 lg:tw-gap-2">
                                <span class="tw-text-lg tw-leading-base tw-font-semibold">{{ subItem.title }}</span>
                                <dk-button
                                    v-if="subItem.href"
                                    :href="subItem.href"
                                    variant="ghost"
                                    class="!tw-text-brand-primary tw-w-fit !tw-px-0 tw-text-sm tw-leading-sm"
                                >
                                    {{ $t('ds-offer-card.see-other-details') }}
                                </dk-button>
                            </div>
                            <div
                                class="dk-offer-card__accordion-item-partner tw-flex tw-flex-col lg:tw-flex-row tw-justify-center tw-items-start lg:tw-items-center tw-gap-4"
                            >
                                <dk-badge
                                    v-if="
                                        subItem.badge &&
                                            subItem.badge.length &&
                                            subItem.badge[0].id &&
                                            subItem.badge[0].label
                                    "
                                    size="small"
                                    :name="subItem.badge[0].id"
                                    :has-icon="false"
                                >
                                    <span class="tw-uppercase">{{ subItem.badge[0].label }}</span>
                                </dk-badge>
                                <div
                                    class="dk-offer-card__accordion-item-offer max-lg:tw-w-full tw-flex tw-justify-between lg:tw-justify-center tw-items-center lg:tw-gap-4"
                                >
                                    <div class="dk-offer-card__accordion-item-price tw-flex tw-items-end tw-gap-1">
                                        <span
                                            class="dk-offer-card__accordion-item-price-final tw-text-base tw-leading-base tw-font-bold"
                                        >
                                            {{ subItem.finalPrice || subItem.originalPrice }}
                                        </span>
                                        <span
                                            class="dk-offer-card__accordion-item-price-suffix tw-mb-[2px] tw-text-xs tw-leading-xs"
                                        >
                                            {{ subItem.priceSuffix }}
                                        </span>
                                    </div>
                                    <img
                                        v-if="subItem.offerLogo && subItem.offerLogo.url"
                                        class="dk-offer-card__accordion-item-partner-logo tw-mx-1 tw-w-auto max-lg:tw-max-h-10 lg:tw-h-10 max-lg:tw-max-w-[130px]"
                                        :src="subItem.offerLogo.url"
                                        :alt="subItem.offerLogo.text"
                                    >
                                </div>
                                <dk-button
                                    class="dk-offer-card__accordion-item-button max-lg:tw-w-full lg:tw-min-w-[192px]"
                                    variant="tertiary"
                                    :is-small="true"
                                    @click="onSelect({ ...subItem, index: index + 1 }, $event)"
                                >
                                    {{ btnText(subItem.selected) }}
                                </dk-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import gaEventHandler from '@v4/common/components/mixins/google-analytics';

export default {
    name: 'DkOfferCard',
    components: {
        DkBadge: () => ({
            component: import(/* webpackChunkName: "DkBadgeDST-vue" */ '@v4/common/components/atoms/DkBadgeDST.vue'),
        }),
        DkIcon: () => ({
            component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
        }),
        DkButton: () => ({
            component: import(/* webpackChunkName: "DkButton-vue" */ '@v4/common/components/atoms/DkButton.vue'),
        }),
    },
    mixins: [gaEventHandler],
    props: {
        /**
         * Item data
         */
        item: {
            type: Object,
            default: () => ({}),
        },
        /**
         * List of all items. Used mainly for the deselection
         */
        itemList: {
            type: Array,
            default: () => [],
        },
        /**
         * The index of the item in the list
         */
        itemIndex: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            collapsed: true,
        };
    },
    computed: {
        items() {
            return this.item.items || [];
        },
        hasItems() {
            return !!this.items.length;
        },
        firstItem() {
            if (this.hasItems) {
                return this.items[0];
            }

            return {};
        },
        subItems() {
            return this.items.filter((item, index) => index !== 0);
        },
        firstItemHasBadge() {
            return this.firstItem.type && this.firstItem.type.text && this.firstItem.type.slug;
        },
        firstItemHasDetails() {
            return this.firstItem.details && !!this.firstItem.details.length;
        },
        downPaymentLabel() {
            if (this.firstItem.details) {
                const downPayment = this.firstItem.details.filter((item) => item.type === 'downPayment')[0];
                if (downPayment) {
                    return downPayment.text;
                }
                return '';
            }
            return '';
        },
        downPaymentLabelPrefix() {
            return this.downPaymentLabel.substring(0, this.downPaymentLabel.indexOf('€')).trim();
        },
        downPaymentLabelText() {
            return `€${this.downPaymentLabel.split('€')[1]}`;
        },
        firstItemHasBenefits() {
            return this.firstItem.benefits && !!this.firstItem.benefits.length;
        },
        firstItemBenefits() {
            if (this.firstItemHasBenefits) {
                return this.firstItem.benefits;
            }

            return [];
        },
    },
    methods: {
        btnText(selected) {
            if (this.disableMultiSelection) {
                return this.$t('ds-offer-card.select');
            }

            return this.$t(`ds-offer-card.${selected ? 'selected' : 'select'}`);
        },
        toggleCollapse() {
            this.collapsed = !this.collapsed;
        },
        isPriceTypeContract(type) {
            return type && typeof type === 'string' && type.toLowerCase() === 'contract_hire';
        },
        isContractHire(item) {
            return item.type && this.isPriceTypeContract(item.type.slug);
        },
        onSelect(item, event) {
            if (item.selected) {
                /**
                 * Fired when an item was re-selected
                 *
                 * @event re-select
                 * @type {object}
                 */
                return this.$emit('re-select', { item, event });
            }

            // handle selection
            if (this.itemList.length) {
                // deselect all
                this.itemList.forEach((item) => item.items.forEach((subItem) => this.$set(subItem, 'selected', false)));
                // select the new one
                this.$set(this.item.items[0], 'selected', true);
            }

            // track cta buttons click
            this.pushGaEvent(
                'vehicle offer page',
                'CTA click',
                !item.index
                    ? `VOP - ${this.itemPosition} box CTA button click`
                    : `VOP - ${this.itemPosition} box ${item.index} sub-item CTA click`
            );

            if (this.isContractHire(item)) {
                this.pushGaEvent('vehicle offer page', 'ContractHire_VOP_select', 'ContractHire_VOP_select');
            }

            /**
             * Fired when an item was selected
             *
             * @event select
             * @type {object}
             */
            this.$emit('select', { item, event });
        },
        trackSeeOtherDetails(item, index) {
            this.pushGaEvent(
                'vehicle offer page',
                'CTA click',
                !index
                    ? `VOP - ${this.itemPosition} box "See more" click`
                    : `VOP - ${this.itemPosition} box ${index} sub-item "See more" click`
            );
            if (this.isContractHire(item)) {
                this.pushGaEvent(
                    'vehicle offer page',
                    'ContractHire_VOP_seedetails_click',
                    'ContractHire_VOP_seedetails_click'
                );
            }
        },
        trackHighlightedPrice(item) {
            if (!this.isContractHire(item)) {
                return;
            }

            this.pushGaEvent(
                'vehicle offer page',
                'ContractHire_VOP_highlight_click',
                'ContractHire_VOP_highlight_click'
            );
        },
        trackContractDisclaimerTextClick() {
            this.pushGaEvent('vehicle offer page', 'ContractHire_VOP_CTA_click', 'ContractHire_VOP_CTA_click');
        },
        openBenefitsModal(item) {
            this.$emit('open-benefits-modal', item);
        },
    },
};
</script>
